import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import HomeIcon from '@mui/icons-material/Home';
import MapIcon from '@mui/icons-material/Map';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import SupportIcon from '@mui/icons-material/Support';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { quikqAuthService } from '../services/quikqAuth';
import { useNavigate } from 'react-router-dom';

interface Props {
  value: number;
  onChange: (event: React.SyntheticEvent, newValue: number) => void;
}

type NavItem = {
  label: string;
  icon: React.ReactNode;
};

// =============================================================================
// Visual design tokens for the bottom nav.
// White bar with a TIGHT semicircle notch wrapping the active donut, plus a
// "donut" active marker (white circle with a thick accent ring + label below
// the notch).
//
// Geometry rule (must hold so the donut fits perfectly inside the notch):
//   NOTCH_RADIUS  = ACTIVE_DIAMETER / 2 + NOTCH_HALO
//   ACTIVE_LIFT   = ACTIVE_DIAMETER - NOTCH_RADIUS
// =============================================================================
const BAR_HEIGHT = 64;                  // height of the white nav bar
const ACTIVE_DIAMETER = 44;             // diameter of the white "donut" circle
const ACTIVE_RING_THICKNESS = 3;        // thickness of the accent ring around the active circle
// NOTCH_HALO=1 keeps just a hairline gap so the curve in the bar visually
// hugs the donut's outline (instead of looking like a wide bowl that the
// donut floats above).
const NOTCH_HALO = 1;                   // px gap between donut edge and notch curve
const NOTCH_RADIUS = ACTIVE_DIAMETER / 2 + NOTCH_HALO; // 22 + 1 = 23 (semicircle radius)
const ACTIVE_LIFT = ACTIVE_DIAMETER - NOTCH_RADIUS;    // 44 - 23 = 21 (px donut pokes above bar)
const ACCENT_COLOR = '#1A5FB4';         // NorthStar blue
const INACTIVE_COLOR = '#7a8290';       // muted gray for inactive icons
const BAR_BG = '#ffffff';
const CORNER_RADIUS = 18;               // top-corner radius on tablet+ widths
// LABEL_GAP=8 drops the active label clearly below the notch so it sits in
// the flat white bar area, not visually tangled with the curve.
const LABEL_GAP = 8;                    // px between donut bottom and label top

/**
 * Build the SVG `path` for the bar background — a rounded-top rectangle with
 * a TRUE semicircle notch dipping into the top edge at `notchCenterX`. The
 * semicircle radius equals `notchRadius`, so the curve follows the outline
 * of the donut sitting in it (donut radius + halo = notchRadius).
 *
 * Traced clockwise so the SVG fill rule produces a clean filled shape with
 * no self-intersections.
 */
function buildBarPath(
  width: number,
  height: number,
  notchCenterX: number,
  notchRadius: number,
  cr: number
): string {
  if (width <= 0) return '';
  const cornerR = Math.min(cr, Math.max(0, (width - 2 * notchRadius) / 4));
  // Clamp the notch center so it never overlaps the rounded corners on
  // very narrow viewports (defensive — only matters < ~250 px wide).
  const cx = Math.min(Math.max(notchCenterX, cornerR + notchRadius), width - cornerR - notchRadius);
  const notchStart = cx - notchRadius;
  const notchEnd = cx + notchRadius;

  const parts: string[] = [];
  parts.push(`M 0 ${cornerR}`);
  if (cornerR > 0) parts.push(`Q 0 0 ${cornerR} 0`);
  else parts.push(`L 0 0`);
  // Top edge → left side of the notch.
  parts.push(`L ${notchStart} 0`);
  // True semicircle dip — `A rx ry x-axis-rot large-arc sweep x y`.
  // sweep=1 (clockwise) makes the arc dip DOWN into the bar (positive Y in
  // SVG = down), matching how we trace the outline clockwise.
  parts.push(`A ${notchRadius} ${notchRadius} 0 0 1 ${notchEnd} 0`);
  // Top edge → right corner.
  parts.push(`L ${width - cornerR} 0`);
  if (cornerR > 0) parts.push(`Q ${width} 0 ${width} ${cornerR}`);
  else parts.push(`L ${width} 0`);
  parts.push(`L ${width} ${height}`);
  parts.push(`L 0 ${height}`);
  parts.push(`Z`);
  return parts.join(' ');
}

/**
 * Renders the white bar (with a smooth curved notch around the active tab)
 * and per-tab "donut" active marker.
 *
 * Implementation notes:
 * - The bar background is an SVG path computed in JS so the notch can be a
 *   true bezier S-curve. The path is rebuilt whenever the active tab or
 *   container width changes.
 * - Each tab has its OWN donut marker that rises slightly when activated and
 *   drops back when deactivated. There is intentionally NO single marker
 *   that slides horizontally between tabs.
 * - Tab buttons live in a separate flex row on top of the SVG so their icons
 *   are never clipped by the bar shape.
 */
const ElevatedBottomNav: React.FC<{
  items: NavItem[];
  activeIndex: number;
  onSelect: (newIndex: number, event: React.SyntheticEvent) => void;
}> = ({ items, activeIndex, onSelect }) => {
  const count = items.length || 1;
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  // Measure container width so the SVG path uses real px (not %) — curves
  // get distorted under `preserveAspectRatio="none"`.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(Math.floor(entry.contentRect.width));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const notchCenterX = ((activeIndex + 0.5) / count) * width;
  // Disable rounded corners on phones (full-width bar), keep them on tablet+.
  const cornerR = width >= 600 ? CORNER_RADIUS : 0;

  const barPath = useMemo(
    () => buildBarPath(width, BAR_HEIGHT, notchCenterX, NOTCH_RADIUS, cornerR),
    [width, notchCenterX, cornerR]
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: BAR_HEIGHT,
        overflow: 'visible',
      }}
    >
      {/* SVG bar — white rounded rectangle with a semicircle notch at the
          active tab. `drop-shadow` follows the entire path edge (so the
          shadow respects the curved notch too) and a thin stroke makes the
          notch outline visible even when the page background behind the bar
          is white (otherwise the white-on-white notch would disappear). */}
      {width > 0 && (
        <svg
          width={width}
          height={BAR_HEIGHT}
          viewBox={`0 0 ${width} ${BAR_HEIGHT}`}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            display: 'block',
            overflow: 'visible',
            filter:
              'drop-shadow(0 -3px 8px rgba(0,0,0,0.16)) drop-shadow(0 -1px 3px rgba(0,0,0,0.10))',
          }}
        >
          <path
            d={barPath}
            fill={BAR_BG}
            stroke="rgba(0, 0, 0, 0.08)"
            strokeWidth={1}
          />
        </svg>
      )}

      {/* Tab buttons — sit on top of the SVG. Each cell renders both its
          inline (inactive) state AND its own "donut" active marker; the two
          cross-fade based on `isActive`. */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'stretch',
        }}
      >
        {items.map((item, idx) => {
          const isActive = idx === activeIndex;
          return (
            <ButtonBase
              key={`${idx}-${item.label}`}
              onClick={(e) => onSelect(idx, e)}
              aria-label={item.label}
              aria-current={isActive ? 'page' : undefined}
              sx={{
                flex: 1,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: INACTIVE_COLOR,
                overflow: 'visible',
                // Hover paints both the inactive icon AND its label blue
                // (the whole `.nsf-bn-inline-icon` block).
                '&:hover .nsf-bn-inline-icon': { color: ACCENT_COLOR },
              }}
            >
              {/* Inactive content — icon + label stacked vertically, both
                  in muted gray. Fades out when the tab is active (since the
                  donut marker takes over visually). Hover turns both blue
                  via the `.nsf-bn-inline-icon` class hook on the parent. */}
              <Box
                className="nsf-bn-inline-icon"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: `${LABEL_GAP}px`,
                  opacity: isActive ? 0 : 1,
                  transition: 'opacity 0.18s ease, color 0.18s ease',
                  color: INACTIVE_COLOR,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    '& svg': { fontSize: { xs: 22, sm: 24, md: 26 } },
                  }}
                >
                  {item.icon}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: { xs: '0.62rem', sm: '0.66rem', md: '0.7rem' },
                    fontWeight: 600,
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.label}
                </Box>
              </Box>

              {/* Active "donut" marker — white circle with accent ring +
                  accent icon inside + accent label below. The donut nests
                  inside the bar's semicircular notch (curve hugs its bottom
                  half), while the LABEL sits below the notch in the plain
                  white bar area. */}
              <Box
                aria-hidden="true"
                sx={{
                  position: 'absolute',
                  // Top of the marker = ACTIVE_LIFT px above the bar's top
                  // edge, so the donut pokes exactly ACTIVE_LIFT px above
                  // and its bottom touches the notch bottom.
                  top: -ACTIVE_LIFT,
                  left: '50%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: `${LABEL_GAP}px`,
                  pointerEvents: 'none',
                  transform: isActive
                    ? 'translate(-50%, 0)'
                    : 'translate(-50%, 6px)',
                  opacity: isActive ? 1 : 0,
                  transition:
                    'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.18s ease',
                }}
              >
                <Box
                  sx={{
                    width: ACTIVE_DIAMETER,
                    height: ACTIVE_DIAMETER,
                    borderRadius: '50%',
                    bgcolor: '#ffffff',
                    border: `${ACTIVE_RING_THICKNESS}px solid ${ACCENT_COLOR}`,
                    color: ACCENT_COLOR,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 10px rgba(26, 95, 180, 0.18)',
                    '& svg': { fontSize: 22 },
                  }}
                >
                  {item.icon}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: { xs: '0.62rem', sm: '0.66rem', md: '0.7rem' },
                    fontWeight: 600,
                    lineHeight: 1,
                    color: ACCENT_COLOR,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.label}
                </Box>
              </Box>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
};

const BottomNav: React.FC<Props> = ({ value, onChange }) => {
  const isDriver = quikqAuthService.isDriver();
  const navigate = useNavigate();
  const [logoutDialogOpen, setLogoutDialogOpen] = React.useState(false);

  const handleLogout = () => setLogoutDialogOpen(true);

  const confirmLogout = () => {
    quikqAuthService.logout();
    navigate('/login');
  };

  const cancelLogout = () => setLogoutDialogOpen(false);

  if (isDriver) {
    const driverItems: NavItem[] = [
      { label: 'Map/Routes', icon: <MapIcon /> },
      { label: 'Support', icon: <SupportIcon /> },
      { label: 'Logout', icon: <LogoutIcon /> },
    ];

    return (
      <>
        <ElevatedBottomNav
          items={driverItems}
          activeIndex={value}
          onSelect={(newValue) => {
            if (newValue === 0) navigate('/map');
            if (newValue === 1) navigate('/support');
            if (newValue === 2) handleLogout();
          }}
        />

        <Dialog open={logoutDialogOpen} onClose={cancelLogout}>
          <DialogTitle>Confirm Logout</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to logout?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={cancelLogout} color="primary">
              Cancel
            </Button>
            <Button onClick={confirmLogout} color="error" variant="contained">
              Logout
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  const regularItems: NavItem[] = [
    { label: 'Dashboard', icon: <HomeIcon /> },
    { label: 'Map/Routes', icon: <MapIcon /> },
    { label: 'Savings', icon: <AccountBalanceIcon /> },
    { label: 'Profile', icon: <PersonIcon /> },
  ];

  return (
    <ElevatedBottomNav
      items={regularItems}
      activeIndex={value}
      onSelect={(newValue, event) => onChange(event, newValue)}
    />
  );
};

export default BottomNav;
